// legacyFrames — shared Win 95 / Java applet 風 border + selection マーカー utility
// (Phase 6.7-B-fix で抽出, Phase 6.7-A 申し送り消化, Phase 6.7-B-fix-2A で
//  selectedMarker を CSS pseudo → SVG component に差し替え).
//
// 旧版 `OekakiChatJava/screenshot/room.png` の左サイドバーは, Win 95 outset frame
// (上左 highlight + 下右 shadow) で囲まれた白背景ボックスに 16 色 / ペン種 / ペン幅 /
// 機能ボタン群が同居する。これを Tailwind class 文字列定数として束ね, 全画面 (Phase
// 6.7-C / D で HomePage / CreateRoomPage / LoginPage / AdminPage / RoomSettingsModal /
// ReJoinScreen) で再利用できるようにする。
//
// 設計メモ:
// - Tailwind v4 の arbitrary value 記法 (`shadow-[inset_1px_1px_0_...]`) で 1 行 inline
//   定義していた 2 つの定数 (outsetFrame / insetFrame) を切り出し。文字列としてそのまま
//   className 末尾に追記する用途を想定。
// - `<SelectedMarker />` は旧版 `PaintToolCanvas.drawPenColorCursor` (line 645-672) を
//   忠実に再現: Color.orange の塗り + Color.black の枠線で左上 + 右下に三角を描く。
//   SVG `<polygon>` で fill + stroke を一発描画でき, CSS border-trick で枠線を出す
//   よりシンプル。Phase 6.7-B では CSS pseudo に挑んだが「黄色の塊にしか見えない」
//   フィードバックで SVG 化に転換 (Phase 6.7-B-fix-2A)。
// - `<SelectedMarker />` 利用側の親要素は `position: relative` 必須 (各 cell の親に
//   付与する)。
//
// 申し送り (Phase 6.7-C / 2B 以降):
// - HomePage / CreateRoomPage / LoginPage / AdminPage の入力枠・パネル枠を順次
//   `outsetFrame` / `insetFrame` に置換することで, 旧版 Java applet 風の統一感を
//   全画面に広げる。ボタン等の "押下中" 表現は `insetFrame`, 通常状態は `outsetFrame`。
// - `<SelectedMarker />` は radio 系 UI 全般で再利用可能 (将来 ARGB Canvas 化で
//   active 値に印を付ける場合等にもそのまま使える)。
// - 色 cell の黒縁取り (`border border-black`) も 16 色 / マスク色 / ARGB Canvas
//   いずれも識別性向上に寄与するので, ARGB Canvas 化時にも踏襲する。

/**
 * 旧版踏襲のフラット枠: 1px 単色枠のみ。
 *
 * Phase 6.7-A で独自に Win 95 outset 演出 (highlight + shadow) を載せていたが,
 * `PaintToolCanvas.java:528-552` (機能ボタン) / `:404-413` (カラーパレット) /
 * `:486-495` (ペン幅 cell) を改めて確認すると, 旧版は `g.drawRect(...)` で
 * **1px 単色枠**のみで描画しており立体感は一切付けていなかった. R16 で本来の
 * フラット見た目に戻す. 押下/選択状態は `bg-[var(--color-accent-soft)]` +
 * `<SelectedMarker />` (黄三角) で識別済なので, border の太さや色で区別する
 * 必要は無い.
 *
 * 通常状態のボックス・ボタン・パネル枠で使用。
 */
export const outsetFrame = 'border border-[var(--color-border)]';

/**
 * 旧版踏襲のフラット枠 (押下/選択中側). 旧版が立体表現を持たないため,
 * `outsetFrame` と完全に同じ 1px 単色枠. 状態の差は背景色 / SelectedMarker で
 * 識別する (R16 — 旧版踏襲の本来の見た目).
 *
 * `outsetFrame` と同じ値だが, 「press / select 用」の意味付けで明示的に
 * 別エクスポートを残す (将来 Modern Layout など別 Layout で立体表現を載せる
 * 可能性があり, その時に `insetFrame` のみ切り替えれば済むため).
 */
export const insetFrame = 'border border-[var(--color-border)]';

/**
 * Phase 6.7 R15: 旧版風 toggle ボタン共通 class.
 *
 * 「機能ボタン (LegacyIconButton)」と「ペン種ボタン (普通/透過)」で見た目を完全に
 * 揃えるための共通 class 文字列ビルダ。状態ごとの色 / frame は以下:
 *
 * - idle (押下なし) : `bg-white text-black` + `outsetFrame`, hover で `accent-soft`
 * - pressed (押下/選択中) : `bg-[var(--color-accent-soft)] text-black` + `insetFrame`
 *   (常時オレンジ薄背景, 凹み)
 * - disabled       : `cursor-not-allowed opacity-50` + idle 見た目固定 (hover 無効)
 *
 * focus ring は旧 Java applet 完全再現要求により全状態で抑制
 * (`focus:outline-none focus-visible:outline-none`)。Tab 巡回時の枠線は出さない。
 *
 * Note: `flex h-[26px] items-center justify-center` 等の "ボタンとしての形状" は呼び出し
 * 側で重ねる (icon ボタンは `h-[26px]` と padding 0.5 — 28→26px の刈り込みは
 * classic-sidebar-compaction.md §7 —, ペン種ボタンは text label なので
 * `px-2 py-1 text-xs`)。本ヘルパは色と frame の状態遷移だけを束ねる。
 */
export const legacyToggleButtonClass = (pressed: boolean, disabled = false): string => {
  const frame = pressed ? insetFrame : outsetFrame;
  const baseColor = pressed ? 'bg-[var(--color-accent-soft)] text-black' : 'bg-white text-black';
  const hoverOrCursor = disabled
    ? 'cursor-not-allowed opacity-50'
    : 'hover:bg-[var(--color-accent-soft)]';
  return `focus:outline-none focus-visible:outline-none ${frame} ${baseColor} ${hoverOrCursor}`;
};

/**
 * 旧版 "選択中" マーカー (左上 + 右下のオレンジ三角 + 黒枠線).
 *
 * 旧版 `PaintToolCanvas.drawPenColorCursor` (PaintToolCanvas.java:645-672) が
 * 選択中 cell の左上 / 右下に, `Color.orange` で塗りつぶし + `Color.black` で
 * 枠線を引く 7px 直角三角形を描いていた。これを SVG `<polygon>` の
 * `fill` + `stroke` 同時指定で再現する。
 *
 * 親要素は `position: relative` 必須 (cell 側で `relative` を付与すること)。
 * pointer-events は `none` で cell 自身の click を邪魔しない。
 *
 * @param size 三角の脚の長さ (px). 旧版は 7px だが視認性で default 8px。
 */
export const SelectedMarker = ({ size = 8 }: { readonly size?: number }): React.ReactNode => (
  <>
    {/* 左上三角: 頂点 (0,0) で右と下に size px. */}
    <svg
      className="pointer-events-none absolute top-0 left-0"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
    >
      <polygon points={`0,0 ${size},0 0,${size}`} fill="#ffa500" stroke="#000" strokeWidth="1" />
    </svg>
    {/* 右下三角: 頂点 (size,size) で左と上に size px. */}
    <svg
      className="pointer-events-none absolute right-0 bottom-0"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
    >
      <polygon
        points={`${size},${size} 0,${size} ${size},0`}
        fill="#ffa500"
        stroke="#000"
        strokeWidth="1"
      />
    </svg>
  </>
);
